import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, X } from 'lucide-react';
import { fetchClubPlayers, fetchClubResearch, type ClubPlayer, type ClubPlayerLeader, type ClubResearchRow } from '@/api/clubResearch';
import ResponsiveStatsTable, { type StatColumn } from '@/components/common/ResponsiveStatsTable';
import PlayerProfileModal from '@/components/common/PlayerProfileModal';
import SafeViewportOverlay from '@/components/common/SafeViewportOverlay';
import SeasonScoreSummary from '@/components/common/SeasonScoreSummary';
import Spinner from '@/components/common/Spinner';
import { useDialogFocus } from '@/hooks/useDialogFocus';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useAppBackButtonInterceptor } from '@/hooks/useAppBackButtonInterceptor';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { formatPlayerDisplayName } from '@/utils/formatting';
import { positionBadgeClasses } from '@/utils/positionColors';
import { normalizedRankCompetitionIds } from '@/utils/playerRank';
import { getAbbreviation } from '@/utils/teams';
import { seasonPillLabel } from '@/utils/seasons';
import { userFacingError } from '@/utils/userFacingError';
import { mixpanelTrack } from '@/services/mixpanel';
import {
  CLUB_SCORING_METRICS, clubLeadersForScope, clubPerGameValue, clubRankColorClass, defaultClubSort, nextClubSort,
  rankClubScope, sortClubRows, type ClubResearchMode,
} from '@/utils/clubResearch';

const metricGroups = [
  { key: 'summary', label: 'Overview' }, { key: 'scoring', label: 'Scoring' },
  { key: 'creating', label: 'Creating' }, { key: 'progression', label: 'Progression' }, { key: 'all', label: 'All' },
];
const fmt = (value: unknown) => typeof value === 'number' ? value.toFixed(1) : '-';
const competitionLabel = (name: string) => /premier/i.test(name) ? 'EPL' : /la liga/i.test(name) ? 'La Liga' : name;
const competitionColor = (name: string) => /premier/i.test(name)
  ? 'border-purple-400/30 bg-purple-500/10 text-purple-300'
  : 'border-orange-400/30 bg-orange-500/10 text-orange-300';
const focusClass = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow';
const positionBadgeClass = 'inline-flex shrink-0 items-center justify-center rounded-md border font-bold';

export default function ClubResearchBrowser({ competitionIds, enabled, leagueId = null }: {
  competitionIds: number[]; enabled: boolean; leagueId?: number | null;
}) {
  const scope = normalizedRankCompetitionIds(competitionIds);
  const scopeKey = scope.join(',');
  const [competition, setCompetition] = useState<number | 'all'>('all');
  const selectedCompetition = competition !== 'all' && !scope.includes(competition) ? 'all' : competition;
  const selectedIds = selectedCompetition === 'all' ? scope : [selectedCompetition];
  const [mode, setMode] = useState<ClubResearchMode>('power');
  const [sort, setSort] = useState(defaultClubSort('power'));
  const [selectedClub, setSelectedClub] = useState<ClubResearchRow | null>(null);
  const [selectedLeader, setSelectedLeader] = useState<{ player: ClubPlayerLeader; club: ClubResearchRow } | null>(null);
  const desktop = useMediaQuery('(min-width: 1024px)');
  const query = useQuery({
    queryKey: ['club-research', scopeKey, desktop],
    queryFn: ({ signal }) => fetchClubResearch(scope, signal, desktop),
    enabled: enabled && scope.length > 0,
    staleTime: 60_000, retry: 1,
    refetchInterval: enabled ? 60_000 : false,
    refetchIntervalInBackground: false,
  });
  useEffect(() => {
    if (enabled && query.error) userFacingError(query.error, null, { section: 'club-research', competitionIds: scopeKey });
  }, [enabled, query.error, scopeKey]);
  useEffect(() => {
    setSelectedClub(null);
    setSelectedLeader(null);
  }, [enabled, scopeKey, selectedCompetition]);
  const rows = useMemo(() => sortClubRows(rankClubScope((query.data?.clubs ?? [])
    .filter(row => selectedCompetition === 'all' || row.competition_id === selectedCompetition)), mode, sort),
  [query.data, selectedCompetition, mode, sort]);
  const columns = useMemo<StatColumn<ClubResearchRow>[]>(() => {
    const headlineRank = mode === 'power' ? 'power_rank' : mode === 'offense' ? 'offense_rank' : 'defense_rank';
    const result: StatColumn<ClubResearchRow>[] = [
      { key: 'club_name', header: 'Club', frozen: true, minWidthPx: 145, sortable: true,
        headerClassName: 'w-40 sm:w-64', cellClassName: '!whitespace-normal', cell: row => (
          <button type="button" onClick={() => {
            setSelectedClub(row);
            mixpanelTrack('Viewed Club Research', { 'Club ID': row.club_id, 'Competition ID': row.competition_id, 'Source': leagueId ? 'League Players' : 'Public Stats' });
          }} className={`flex min-h-11 w-full flex-wrap items-center gap-x-2 gap-y-0.5 text-left text-xs sm:text-sm leading-snug ${focusClass}`}>
            <span className="font-bold text-gray-100">{row.club_name}</span>
            {scope.length > 1 && <span className={`shrink-0 rounded border px-1.5 text-[10px] ${competitionColor(row.competition_name)}`}>{competitionLabel(row.competition_name)}</span>}
          </button>
        ) },
      { key: 'rank', header: 'Rank', align: 'right', widthPx: 52, sortable: true, group: 'id',
        cell: row => <span className="font-bold text-gray-100">{row[headlineRank] ?? '-'}</span> },
    ];
    const pointsColumn = (key: 'points_for_per_match' | 'points_against_per_match' | 'rating', header: string, tooltip: string): StatColumn<ClubResearchRow> => ({
      key, header, tooltip, align: 'right', widthPx: 100, sortable: true, group: 'id',
      cellClassName: 'font-bold', accessor: row => clubPerGameValue(row, key), format: fmt,
    });
    if (mode === 'power') {
      result.push(pointsColumn('rating', 'Rating', 'Fantasy points scored per game minus fantasy points allowed per game. Higher is better.'));
      result.push(pointsColumn('points_for_per_match', 'PTS For', 'Fantasy points scored per game'));
      result.push(pointsColumn('points_against_per_match', 'PTS Against', 'Fantasy points allowed per game'));
      if (desktop) result.push({
        key: 'top_players', header: 'Team Leaders (PPG)', widthPx: 520,
        tooltip: 'Current squad\'s top three by current-season fantasy points, across all positions. PPG is fantasy points per recorded player appearance.',
        cellClassName: '!whitespace-normal', cell: row => (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {clubLeadersForScope(row, selectedIds.length > 1).map(player => (
              <button key={player.id} type="button" onClick={() => {
                setSelectedLeader({ player, club: row });
                mixpanelTrack('Viewed Club Research Player', { 'Player ID': player.id, 'Club ID': row.club_id, 'Source': 'Season Leaders' });
              }} className={`inline-flex min-h-11 items-center gap-1.5 rounded-md px-1 text-left text-xs font-semibold text-white hover:bg-surface-raised/70 ${focusClass}`}>
                <span className={`${positionBadgeClass} ${positionBadgeClasses(player.primary_position)} h-6 w-7 text-[10px]`}>{player.primary_position || '-'}</span>
                <span>{formatPlayerDisplayName(player)}{typeof player.fppg === 'number' && Number.isFinite(player.fppg) && <span className="whitespace-nowrap tabular-nums"> · {fmt(player.fppg)}</span>}</span>
              </button>
            ))}
            {!clubLeadersForScope(row, selectedIds.length > 1).length && <span className="text-xs text-gray-500">-</span>}
          </div>
        ),
      });
    } else {
      const offense = mode === 'offense';
      result.push(pointsColumn(offense ? 'points_for_per_match' : 'points_against_per_match', offense ? 'PTS For' : 'PTS Allowed', 'Actual current-season fantasy points per game'));
      for (const metric of CLUB_SCORING_METRICS) result.push({
        key: metric.key, header: metric.label,
        tooltip: `${metric.name} ${offense ? 'scored' : 'by opponents'} per game`,
        align: 'right', sortable: true, groups: [metric.group, 'all'], cell: row => {
          if (!row.games_played) return '-';
          const rate = clubPerGameValue(row, metric.key, offense ? 'for' : 'against');
          const rank = (offense ? row.stat_ranks_for : row.stat_ranks_against)[metric.key];
          return <span>{fmt(rate)} <span className={`text-[10px] sm:text-xs ${clubRankColorClass(rank, rows.length)}`}>({rank == null ? '-' : `#${rank}`})</span></span>;
        },
      });
    }
    return result;
  }, [mode, scope.length, leagueId, rows.length, desktop, selectedIds.length]);
  const seasons = query.data?.seasons.filter(s => selectedCompetition === 'all' || s.competition_id === selectedCompetition) ?? [];
  const scopeLabel = selectedCompetition === 'all' && scope.length > 1 ? 'All competitions' : competitionLabel(seasons[0]?.competition_name ?? 'Competition');
  const seasonLabel = seasons[0] ? seasonPillLabel(seasons[0].season_identifier) : 'Current season';
  return (
    <section hidden={!enabled} aria-label="Real-club research" className="mx-auto w-full max-w-7xl overflow-hidden rounded-lg border border-gray-700 bg-primary-dark text-gray-200">
      <div className="border-b border-gray-700 px-2 py-2 sm:px-4 sm:py-3">
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1 sm:justify-between">
          <div className="flex w-full max-w-lg gap-1.5 sm:w-auto" role="group" aria-label="Team ranking view">
            {([
              ['power', 'Power Rankings'], ['offense', 'Offense'], ['defense', 'Defense'],
            ] as const).map(([value, label]) => (
              <button type="button" key={value} aria-pressed={mode === value}
                onClick={() => { setMode(value); setSort(defaultClubSort(value)); }}
                className={`min-h-11 flex-1 whitespace-nowrap rounded-full border px-2 sm:px-4 text-xs sm:text-sm font-bold transition-colors duration-150 active:scale-[0.98] ${focusClass} ${mode === value ? 'border-accent-yellow bg-accent-yellow text-primary' : 'border-gray-700 bg-primary text-gray-400 hover:text-white'}`}>
                {label}
              </button>
            ))}
          </div>
          {scope.length > 1 && <div className="flex justify-center gap-1" role="group" aria-label="Team competition filter">
            {[{ id: 'all' as const, label: 'All', name: '' }, ...(query.data?.seasons ?? []).map(s => ({ id: s.competition_id, label: competitionLabel(s.competition_name), name: s.competition_name }))].map(option => (
              <button key={option.id} type="button" aria-pressed={selectedCompetition === option.id}
                onClick={() => setCompetition(option.id)}
                className={`min-h-11 rounded-full px-1 text-xs font-semibold ${focusClass} ${selectedCompetition === option.id ? 'text-accent-yellow' : 'text-gray-500 hover:text-gray-200'}`}>
                <span className={`rounded-full border px-3 py-1.5 ${selectedCompetition === option.id ? 'border-accent-yellow/60 bg-accent-yellow/10' : option.name ? competitionColor(option.name) : 'border-gray-700'}`}>{option.label}</span>
              </button>
            ))}
          </div>}
        </div>
        <p className="mt-1 px-1 text-[11px] sm:text-xs text-gray-400">{seasonLabel} · {scopeLabel} · Per-game averages</p>
      </div>
      {query.isPending || !scope.length ? <div className="flex min-h-64 items-center justify-center gap-3"><Spinner size="md" /><span className="text-sm text-gray-400">Loading teams...</span></div>
        : query.error && !query.data ? <ResearchError onRetry={() => { void query.refetch(); }} retrying={query.isFetching} />
        : <>
          {query.error && <ResearchError onRetry={() => { void query.refetch(); }} retrying={query.isFetching} compact />}
          <ResponsiveStatsTable columns={columns} rows={rows} rowKey={row => `${row.competition_id}:${row.club_id}`}
            sortBy={sort.key} sortOrder={sort.order} onSortChange={key => setSort(previous => nextClubSort(previous, key, mode))}
            ariaLabel={`${scopeLabel} ${mode} rankings`} mobileGroups={mode === 'power' ? undefined : metricGroups}
            mobileGroupVariant="tabs" initialMobileGroup="summary" emptyMessage="No team statistics are available yet." />
          <p className="border-t border-gray-700 px-3 py-2 text-[10px] sm:text-xs text-gray-500">
            Completed, fully scored matches only.{(query.data?.pending_matches ?? 0) > 0 ? ' Recent final matches are still being scored.' : ''}
          </p>
        </>}
      <ClubDialog club={enabled ? selectedClub : null} competitionIds={selectedIds} leagueId={leagueId} onClose={() => setSelectedClub(null)} />
      <PlayerProfileModal isOpen={enabled && !!selectedLeader} player={selectedLeader?.player ?? null} onClose={() => setSelectedLeader(null)}
        competitionId={selectedLeader?.club.competition_id ?? null} competitionName={selectedLeader?.club.competition_name}
        rankCompetitionIds={selectedIds} leagueId={leagueId} pinCompetition manageFocus />
    </section>
  );
}

function ResearchError({ onRetry, retrying, compact = false }: { onRetry: () => void; retrying: boolean; compact?: boolean }) {
  return <div className={`text-center ${compact ? 'border-b border-gray-700 p-3' : 'px-4 py-12'}`} role="status">
    <p className="text-sm text-gray-400">{compact ? 'The latest team stats are temporarily unavailable. Showing the last loaded results.' : 'Team stats are temporarily unavailable.'}</p>
    <button type="button" onClick={onRetry} disabled={retrying} className={`mt-2 min-h-11 rounded-lg border border-gray-600 px-4 text-sm font-bold text-accent-light-blue disabled:opacity-50 ${focusClass}`}>{retrying ? 'Trying again...' : 'Try again'}</button>
  </div>;
}

function ClubDialog({ club, competitionIds, leagueId, onClose }: {
  club: ClubResearchRow | null; competitionIds: number[]; leagueId: number | null; onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const scopeKey = competitionIds.join(',');
  const [profile, setProfile] = useState<ClubPlayer | null>(null);
  const [scorePlayer, setScorePlayer] = useState<ClubPlayer | null>(null);
  const closeTop = () => { if (scorePlayer) setScorePlayer(null); else onClose(); };
  useBodyScrollLock(!!club);
  useDialogFocus(ref, !!club, closeTop, !!profile);
  useAppBackButtonInterceptor(!!club, closeTop);
  useEffect(() => { setProfile(null); setScorePlayer(null); }, [club?.club_id, scopeKey]);
  useEffect(() => { if (scorePlayer) backRef.current?.focus(); }, [scorePlayer]);
  const query = useQuery({
    queryKey: ['club-players', club?.club_id, scopeKey],
    queryFn: ({ signal }) => fetchClubPlayers(club!.club_id, competitionIds, signal),
    enabled: !!club, staleTime: 60_000, retry: 1,
  });
  useEffect(() => {
    if (club && query.error) userFacingError(query.error, null, { section: 'club-players', clubId: club.club_id, competitions: scopeKey });
  }, [club, query.error, scopeKey]);
  if (!club) return null;
  const totalContext = query.data?.seasons.map(s => competitionLabel(s.competition_name)).join(' + ') ?? competitionLabel(club.competition_name);
  return <>
    <SafeViewportOverlay onClose={closeTop} spacing="compact" className="items-center justify-center" zIndexClassName="z-[60]" closeOnBackdrop={!profile}>
      <div ref={ref} tabIndex={-1} role="dialog" aria-modal={!profile} aria-labelledby="club-research-title" inert={!!profile}
        className="relative flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-gray-700/50 bg-surface-card shadow-xl outline-none sm:max-h-[85dvh]">
        <header className="relative flex shrink-0 items-start justify-center border-b border-gray-700 bg-primary-dark px-14 py-4">
          <div className="min-w-0 text-center">
            {scorePlayer && <button ref={backRef} type="button" onClick={() => setScorePlayer(null)} className={`mb-1 inline-flex min-h-9 items-center gap-1 text-xs font-bold text-accent-light-blue ${focusClass}`}><ArrowLeft size={14} />{club.club_name}</button>}
            <h2 id="club-research-title" className="text-2xl font-heading font-bold text-white break-words">{scorePlayer ? formatPlayerDisplayName(scorePlayer) : club.club_name}</h2>
            <p className="mt-1 text-xs text-gray-400">{scorePlayer ? 'Season score breakdown' : `${competitionLabel(club.competition_name)} · Current squad`}</p>
          </div>
          <button type="button" aria-label={scorePlayer ? 'Back to club squad' : 'Close club details'} onClick={closeTop} className={`absolute right-1 top-1 flex min-h-11 min-w-11 items-center justify-center rounded-full text-gray-400 hover:bg-primary/50 hover:text-white ${focusClass}`}><X size={22} /></button>
        </header>
        <div className="min-h-0 overflow-y-auto overscroll-contain">
          {scorePlayer ? <div className="px-4 py-3">
            <p className="mb-3 text-xs text-gray-400">{seasonPillLabel(club.season_identifier)} · {totalContext}</p>
            <SeasonScoreSummary counts={scorePlayer.stat_counts} actualPoints={scorePlayer.stat_points} />
          </div> : <>
            <div className="flex items-center justify-between gap-3 border-b border-gray-700/50 bg-primary/20 px-3 py-2 text-[11px] font-semibold">
              <span className="text-accent-light-blue">{seasonPillLabel(club.season_identifier)} · {totalContext}</span>
              <span className="shrink-0 uppercase tracking-wide text-gray-500">Season points</span>
            </div>
            {query.isPending ? <div className="flex min-h-40 items-center justify-center"><Spinner size="md" /></div>
              : query.error && !query.data ? <ResearchError onRetry={() => { void query.refetch(); }} retrying={query.isFetching} />
              : <>
                {query.error && <ResearchError onRetry={() => { void query.refetch(); }} retrying={query.isFetching} compact />}
                <ul>
                  {query.data?.players.map(player => <li key={player.id} className="flex min-h-14 items-center gap-2 border-b border-accent-light-blue/10 bg-primary-dark/45 px-2.5 py-1.5 odd:bg-primary/25 last:border-b-0 sm:px-3">
                    <button type="button" onClick={() => setProfile(player)} className={`group flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-md text-left hover:bg-surface-raised/70 ${focusClass}`}>
                      <span className={`${positionBadgeClass} ${positionBadgeClasses(player.primary_position)} h-8 w-10 text-xs`}>{player.primary_position || '-'}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block break-words text-sm font-semibold leading-tight text-white">{formatPlayerDisplayName(player)}</span>
                        <span className="mt-1 block text-xs font-semibold text-accent-light-blue">{getAbbreviation(player.current_team_name)}</span>
                      </span>
                    </button>
                    <button type="button" aria-label={`View ${formatPlayerDisplayName(player)} season score breakdown`} onClick={() => setScorePlayer(player)} className={`group/score min-h-11 min-w-16 shrink-0 rounded-md bg-accent-yellow/10 px-2 py-1 text-right ring-1 ring-inset ring-accent-yellow/40 transition-colors duration-150 hover:bg-accent-yellow active:bg-accent-yellow ${focusClass}`}>
                      <span className="block text-lg font-heading font-bold leading-none text-accent-yellow tabular-nums group-hover/score:text-primary-dark group-active/score:text-primary-dark">{player.fantasy_points.toFixed(1)}</span>
                      <span className="mt-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400 group-hover/score:text-primary-dark/75 group-active/score:text-primary-dark/75">FPTS</span>
                    </button>
                  </li>)}
                {query.data?.players.length === 0 && <li className="py-10 text-center text-sm text-gray-400">No current squad players are available.</li>}
                </ul>
                <p className="border-t border-gray-700/50 px-3 py-2 text-[10px] text-gray-500">Player points include matches for previous clubs within {totalContext}.</p>
              </>}
          </>}
        </div>
      </div>
    </SafeViewportOverlay>
    <PlayerProfileModal isOpen={!!profile} player={profile} onClose={() => setProfile(null)} competitionId={club.competition_id}
      competitionName={club.competition_name} rankCompetitionIds={competitionIds} leagueId={leagueId} pinCompetition isStacked manageFocus />
  </>;
}
